export interface Node {
  id: string;
  name: string | { title: string; explanation: string };
  type: 'post' | 'trigger' | 'relation';
  imageUrl?: string;
  imageFileName?: string;
  timestamp?: string;
  rate?: number;
  subjectId?: string;
  feedbackAccuracy?: boolean;
  feedbackClarity?: boolean;
  feedbackNovelty?: boolean;
  feedbackNoveltyDetail?: string;
}

export interface Link {
  source: string;
  target: string;
}

export interface GraphData {
  nodes: Node[];
  links: Link[];
}

// 新的数据结构接口
export interface Post {
  id: string;
  name: string;
  imageUrl: string;
  imageFileName?: string;
  timestamp: string;
  triggers: Trigger[];
  rate?: number;
  subjectId?: string;
  feedback?: {
    accuracy?: boolean;
    clarity?: boolean;
    novelty?: boolean;
    noveltyDetail?: string;
  };
  rawAnalysisResult?: any;
}

export interface Trigger {
  id: string;
  trigger_id?: string; // 数据库中的真实 trigger ID，用于 API 调用
  title: string;
  explanation: string;
  type?: string;
  confidence?: number;
  isAccepted?: boolean;
  rating?: number | null;
}

export interface Relation {
  id: string;
  name: string;
  triggers: string[];
  description: string;
  color: string;
}

export interface NewDataStructure {
  posts: Post[];
  relations: Relation[];
}

// 更新图数据
export const updateGraphData = (existingData: GraphData, newData: any) => {
  const { postId, keyword, triggers } = newData;
  
  // 移除该帖子的旧数据
  existingData.nodes = existingData.nodes.filter(node => !node.id.startsWith(postId));
  existingData.links = existingData.links.filter(link => !link.source.startsWith(postId) && !link.target.startsWith(postId));
  
  // 添加关键词节点
  const keywordNode: Node = {
    id: `${postId}_keyword`,
    name: keyword,
    type: 'post'
  };
  
  // 添加trigger节点和链接
  const triggerNodes: Node[] = triggers.map((trigger: any, index: number) => ({
    id: `${postId}_trigger_${index}`,
    name: trigger,
    type: 'trigger'
  }));
  
  const triggerLinks: Link[] = triggers.map((_: any, index: number) => ({
    source: `${postId}_keyword`,
    target: `${postId}_trigger_${index}`
  }));
  
  // 更新数据
  existingData.nodes.push(keywordNode, ...triggerNodes);
  existingData.links.push(...triggerLinks);
  
  return existingData;
};

// 将旧数据结构转换为新数据结构
export const convertToNewDataStructure = (oldData: GraphData): NewDataStructure => {
  const newData: NewDataStructure = {
    posts: [],
    relations: []
  };
  
  // 获取所有帖子节点
  const postNodes = oldData.nodes.filter(node => node.type === 'post');
  
  // 为每个帖子创建新的数据结构
  postNodes.forEach(postNode => {
    // 获取与该帖子相关的所有链接
    const postLinks = oldData.links.filter(link => link.source === postNode.id);
    
    // 获取与该帖子相关的所有触发器
    const postTriggers: Trigger[] = postLinks.map(link => {
      const triggerNode = oldData.nodes.find(node => node.id === link.target);
      if (triggerNode && triggerNode.type === 'trigger') {
        const triggerName = typeof triggerNode.name === 'string' 
          ? { title: triggerNode.name, explanation: '' } 
          : triggerNode.name;
        
        return {
          id: triggerNode.id,
          title: triggerName.title,
          explanation: triggerName.explanation
        };
      }
      return null;
    }).filter(Boolean) as Trigger[];
    
    // 创建新的帖子对象
    const newPost: Post = {
      id: postNode.id,
      name: typeof postNode.name === 'string' ? postNode.name : postNode.name.title,
      imageUrl: postNode.imageUrl || '',
      imageFileName: postNode.imageFileName,
      timestamp: postNode.timestamp || new Date().toISOString(),
      triggers: postTriggers,
      rate: postNode.rate,
      subjectId: postNode.subjectId,
      feedback: {
        accuracy: postNode.feedbackAccuracy,
        clarity: postNode.feedbackClarity,
        novelty: postNode.feedbackNovelty,
        noveltyDetail: postNode.feedbackNoveltyDetail
      }
    };
    
    newData.posts.push(newPost);
  });
  
  return newData;
};

// 将新数据结构转换为旧数据结构（用于向后兼容）
export const convertToOldDataStructure = (newData: NewDataStructure): GraphData => {
  const oldData: GraphData = {
    nodes: [],
    links: []
  };
  
  // 处理帖子
  newData.posts.forEach(post => {
    // 添加帖子节点
    const postNode: Node = {
      id: post.id,
      name: post.name,
      type: 'post',
      imageUrl: post.imageUrl,
      imageFileName: post.imageFileName,
      timestamp: post.timestamp,
      rate: post.rate,
      subjectId: post.subjectId,
      feedbackAccuracy: post.feedback?.accuracy,
      feedbackClarity: post.feedback?.clarity,
      feedbackNovelty: post.feedback?.novelty,
      feedbackNoveltyDetail: post.feedback?.noveltyDetail
    };
    oldData.nodes.push(postNode);
    
    // 添加触发器节点和链接
    post.triggers.forEach((trigger) => {
      const triggerNode: Node = {
        id: trigger.id,
        name: { title: trigger.title, explanation: trigger.explanation },
        type: 'trigger'
      };
      oldData.nodes.push(triggerNode);
      
      const link: Link = {
        source: post.id,
        target: trigger.id
      };
      oldData.links.push(link);
    });
  });
  
  // 处理关系
  newData.relations.forEach(relation => {
    // 添加关系节点
    const relationNode: Node = {
      id: relation.id,
      name: relation.name,
      type: 'relation'
    };
    oldData.nodes.push(relationNode);
    
    // 添加关系与触发器的链接
    relation.triggers.forEach(triggerId => {
      const link: Link = {
        source: relation.id,
        target: triggerId
      };
      oldData.links.push(link);
    });
  });
  
  return oldData;
};

// 保存分析数据
export const saveAnalysisData = async (
  postId: string, 
  keyword: string, 
  triggersFromAnalysis: Trigger[],
  imageUrl?: string, 
  imageFileName?: string,
  subjectId?: string,
  feedbackAccuracy?: boolean,
  feedbackClarity?: boolean,
  feedbackNovelty?: boolean,
  feedbackNoveltyDetail?: string,
  rawAnalysisResult?: any,
  anxietyLevel?: number
) => {
  try {
    // Load existing full data structure from localStorage, or initialize if not present
    let allData: NewDataStructure;
    try {
      const existingDataString = localStorage.getItem('allAnxietyData');
      if (existingDataString) {
        allData = JSON.parse(existingDataString);
        console.log('[saveAnalysisData] Loaded existing allData from localStorage:', 
                      JSON.stringify({ postsCount: allData.posts.length, relationsCount: allData.relations.length, firstPostId: allData.posts[0]?.id }, null, 2));
      } else {
        allData = { posts: [], relations: [] };
        console.log('[saveAnalysisData] Initialized new allData as localStorage item was not found.');
      }
    } catch (e) {
      console.error('[saveAnalysisData] Error parsing allAnxietyData from localStorage, re-initializing:', e);
      allData = { posts: [], relations: [] };
    }

    let savedImagePath = imageUrl; 
    let actualImageFileName = imageFileName; 

    if (imageUrl && imageUrl.startsWith('data:image')) {
      try {
        const imageResponse = await fetch('/api/save-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            imageData: imageUrl,
            postId: subjectId ? `${subjectId}_${postId}` : postId,
            originalFileName: imageFileName 
          }),
        });
        if (imageResponse.ok) {
          const imageResult = await imageResponse.json();
          savedImagePath = imageResult.imagePath; 
        } else {
          console.error('Failed to save image, using original or no image path.');
        }
      } catch (imageError) {
        console.error('Error saving image:', imageError);
      }
    }

    const postIndex = allData.posts.findIndex(p => p.id === postId);
    if (postIndex > -1) {
      const existingPost = allData.posts[postIndex];
      const updatedPostData: Post = {
        ...existingPost,
        id: postId,
        name: keyword,
        imageUrl: savedImagePath !== undefined ? (savedImagePath || '') : existingPost.imageUrl,
        imageFileName: actualImageFileName !== undefined ? actualImageFileName : existingPost.imageFileName,
        timestamp: existingPost.timestamp || new Date().toISOString(),
        triggers: triggersFromAnalysis,
        rate: anxietyLevel !== undefined ? anxietyLevel : existingPost.rate,
        subjectId: subjectId !== undefined ? subjectId : existingPost.subjectId,
        feedback: {
          accuracy: feedbackAccuracy !== undefined ? feedbackAccuracy : existingPost.feedback?.accuracy,
          clarity: feedbackClarity !== undefined ? feedbackClarity : existingPost.feedback?.clarity,
          novelty: feedbackNovelty !== undefined ? feedbackNovelty : existingPost.feedback?.novelty,
          noveltyDetail: feedbackNoveltyDetail !== undefined ? feedbackNoveltyDetail : existingPost.feedback?.noveltyDetail,
        },
        rawAnalysisResult: rawAnalysisResult !== undefined ? rawAnalysisResult : existingPost.rawAnalysisResult,
      };
      allData.posts[postIndex] = updatedPostData;
    } else {
      const newPostEntry: Post = {
        id: postId,
        name: keyword,
        imageUrl: savedImagePath || '',
        imageFileName: actualImageFileName,
        timestamp: new Date().toISOString(),
        triggers: triggersFromAnalysis,
        rate: anxietyLevel,
        subjectId: subjectId,
        feedback: {
          accuracy: feedbackAccuracy,
          clarity: feedbackClarity,
          novelty: feedbackNovelty,
          noveltyDetail: feedbackNoveltyDetail
        },
        rawAnalysisResult: rawAnalysisResult
      };
      allData.posts.push(newPostEntry);
    }

    // Save the updated full data structure back to localStorage
    try {
      localStorage.setItem('allAnxietyData', JSON.stringify(allData));
      console.log('Analysis data saved successfully to localStorage.');

      // 注释掉markdown报告更新，因为API不存在
      // if (subjectId) {
      //   try {
      //     const currentPost = allData.posts.find(p => p.id === postId);
      //     if (currentPost) {
      //       const response = await fetch('/api/export-markdown', {
      //         method: 'POST',
      //         headers: {
      //           'Content-Type': 'application/json',
      //         },
      //         body: JSON.stringify({ 
      //           userIdentity: JSON.parse(localStorage.getItem('userIdentity') || '{}'),
      //           postsToExport: [currentPost]
      //         }),
      //       });
      //       const result = await response.json();
      //       if (response.ok) {
      //         console.log('Markdown report updated successfully:', result.reportPath);
      //       } else {
      //         throw new Error(result.details || result.error || 'Failed to update markdown report');
      //       }
      //     }
      //   } catch (markdownError) {
      //     console.error('Error updating markdown report:', markdownError);
      //   }
      // }

      // 注释掉服务器端数据保存，因为API不存在
      // try {
      //   const filteredDataForViz: NewDataStructure = { posts: [], relations: allData.relations };
      //   allData.posts.forEach(post => {
      //     const acceptedTriggers = post.triggers.filter(trigger => trigger.isAccepted === true);
      //     if (acceptedTriggers.length > 0) {
      //       filteredDataForViz.posts.push({ ...post, triggers: acceptedTriggers });
      //     } else {
      //       console.log(`Post ${post.id} has no accepted triggers, excluding from anxiey-data.json`);
      //     }
      //   });

      //   if (filteredDataForViz.posts.length > 0 || filteredDataForViz.relations.length > 0) {
      //     const serverSaveResponse = await fetch('/api/save-anxiety-data', {
      //       method: 'POST',
      //       headers: {
      //         'Content-Type': 'application/json',
      //       },
      //       body: JSON.stringify(filteredDataForViz),
      //     });
      //     if (serverSaveResponse.ok) {
      //       console.log('Filtered analysis data for visualization also saved successfully to server (anxiey-data.json).');
      //     } else {
      //       const errorResult = await serverSaveResponse.json().catch(() => ({ details: 'Server save failed with no details' }));
      //       console.error('Failed to save filtered analysis data to server:', errorResult.details || serverSaveResponse.statusText);
      //     }
      //   } else {
      //     console.log('No accepted posts or relations to save to anxiey-data.json');
      //   }
      // } catch (serverSaveError) {
      //   console.error('Error during fetch to /api/save-anxiety-data for filtered data:', serverSaveError);
      // }

    } catch (saveError) {
      console.error('Error saving data to localStorage:', saveError);
      throw saveError;
    }

    // Optional: update old GraphData in localStorage for backward compatibility
    try {
      const existingLocalGraphData: GraphData = JSON.parse(localStorage.getItem('anxietyData') || '{"nodes":[],"links":[]}');
      
      const currentPostForOldStructure = postIndex > -1 ? allData.posts[postIndex] : allData.posts[allData.posts.length - 1];

      const tempPostGraphNode: Node = {
        id: currentPostForOldStructure.id,
        name: currentPostForOldStructure.name,
        type: 'post',
        imageUrl: currentPostForOldStructure.imageUrl,
        imageFileName: currentPostForOldStructure.imageFileName,
        timestamp: currentPostForOldStructure.timestamp,
        rate: currentPostForOldStructure.rate,
        subjectId: currentPostForOldStructure.subjectId,
        feedbackAccuracy: currentPostForOldStructure.feedback?.accuracy,
        feedbackClarity: currentPostForOldStructure.feedback?.clarity,
        feedbackNovelty: currentPostForOldStructure.feedback?.novelty,
        feedbackNoveltyDetail: currentPostForOldStructure.feedback?.noveltyDetail
      };
      const tempTriggerNodes: Node[] = currentPostForOldStructure.triggers.map(t => ({
        id: t.id,
        name: { title: t.title, explanation: t.explanation },
        type: 'trigger'
      }));
      const tempLinks: Link[] = currentPostForOldStructure.triggers.map(t => ({
        source: currentPostForOldStructure.id,
        target: t.id
      }));

      existingLocalGraphData.nodes = existingLocalGraphData.nodes.filter(n => n.id !== currentPostForOldStructure.id && !currentPostForOldStructure.triggers.find(t => t.id === n.id));
      existingLocalGraphData.links = existingLocalGraphData.links.filter(l => l.source !== currentPostForOldStructure.id);
      
      existingLocalGraphData.nodes.push(tempPostGraphNode, ...tempTriggerNodes);
      existingLocalGraphData.links.push(...tempLinks);

      localStorage.setItem('anxietyData', JSON.stringify(existingLocalGraphData));
      console.log('Data (old format) also updated in localStorage.');
    } catch (localSaveError) {
      console.warn('Could not update localStorage with GraphData:', localSaveError);
    }

  } catch (error) {
    console.error('Error in saveAnalysisData:', error);
    throw error;
  }
};

// 导出数据 - THIS FUNCTION WILL BE REMOVED
/*
export const exportData = async () => {
  try {
    const response = await fetch('/api/load-anxiety-data');
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load data from server: ${response.status} ${errorText}`);
    }
    const data: NewDataStructure = await response.json();

    if (!data || data.posts.length === 0) {
      alert('No data to export.'); // Or handle this more gracefully
      console.log('No data available to export.');
      return;
    }

    const jsonData = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'anxiety_data_export.json'; // Changed filename to reflect new structure
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    console.log('Data exported successfully.');
  } catch (error) {
    console.error('Error exporting data:', error);
    alert(`Error exporting data: ${error instanceof Error ? error.message : String(error)}`);
  }
};
*/

// 获取焦虑数据 - This function might also need an update if it was relying on server data
// For now, let's assume it should primarily fetch from the new 'allAnxietyData' localStorage key
export async function getAnxietyData(): Promise<NewDataStructure> {
  try {
    // 直接从localStorage加载数据
    const localDataString = localStorage.getItem('allAnxietyData');
    if (localDataString) {
      const localData = JSON.parse(localDataString);
      if (localData && typeof localData.posts !== 'undefined' && typeof localData.relations !== 'undefined') {
        console.log('Successfully loaded data from localStorage (allAnxietyData)');
        return localData as NewDataStructure;
      }
    }
    
    // 如果没有新格式数据，尝试从旧格式localStorage加载
    const oldDataString = localStorage.getItem('anxietyData');
    if (oldDataString) {
      const oldData = JSON.parse(oldDataString);
      if (oldData && oldData.nodes && oldData.links) {
        console.log('Converting old format data from localStorage to new format');
        return convertToNewDataStructure(oldData);
      }
    }
    
    console.log('No data found in localStorage, returning empty structure');
    return { posts: [], relations: [] };
  } catch (error) {
    console.error('Error loading data from localStorage:', error);
    return { posts: [], relations: [] };
  }
} 